import type { HTMLAttributes, ElementType, ReactNode } from 'react';

import { memo, forwardRef, useMemo } from 'react';

import classy from '@core/utils/classy';
import Box from '@ui/Box';

import './style.scss';

type Props = HTMLAttributes<HTMLElement> & {
  TagName?: ElementType;
  children?: ReactNode;
  className?: string;
  /**
   * Max height for the menu. When set, the menu scrolls vertically once its
   * content exceeds this height. Accepts any valid CSS height value (e.g. `300`, `'20rem'`).
   */
  maxHeight?: number | string;
  theme?: 'dark' | 'light';
};

const Menu = memo(
  forwardRef<HTMLElement, Props>(
    ({ children, className = '', maxHeight, style, theme = 'light', TagName = 'div', ...attrs }: Props, ref) => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const _className = useMemo(() => classy('Menu', `Menu_${theme}`, className), [className]);

      const _style = useMemo(
        () => (maxHeight !== undefined ? { ...style, maxHeight, overflowY: 'auto' as const } : style),
        [maxHeight, style],
      );

      return (
        <Box
          className={_className}
          kind="pop"
          style={_style}
          Tag={TagName}
          theme={theme}
          {...attrs}
          {...(theme === 'dark' && { 'data-color-mode': 'dark' })}
          ref={ref}
        >
          {children}
        </Box>
      );
    },
  ),
);

export { default as MenuDivider } from '@ui/Menu/Divider';
export { default as MenuHeader } from '@ui/Menu/Header';
export { default as MenuItem } from '@ui/Menu/Item';

export default Menu;
